import { type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  CardIcon,
  EditIcon,
  PackageIcon,
  Upload2Icon,
} from '@/components/Icons'
import { MoreHorizontal, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Link, useNavigate } from 'react-router-dom'
import { PATHROUTES } from '@/constants/path'
import {
  DISPLAY_SPONSORSHIP_STATUS,
  type DisplaySponsorEvent,
} from '@/schemas/sponsor.schems'
import { capitalizeString, paymentStatusClasses } from '@/utils/helper'
import { getSortingFn } from '@/utils/columnUtils'
import { DATATYPE } from '@/constants'
import {
  isEventCompleted,
  sponsorEventListAccessorKey,
} from '@/constants/sponsor'

export type SponsorshipStatus =
  | 'Active'
  | 'Paid'
  | 'Sponsorship Requested'
  | 'Not Selected'

export type SponsorshipTier = 'Bronze' | 'Silver' | 'Gold' | '-'

export interface SponsorshipEvent {
  id: string
  eventName: string
  sponsorshipTier: SponsorshipTier
  startDate: string
  endDate: string
  location: string
  sponsorshipStatus: SponsorshipStatus
}

interface SponsorShipActionsMenuProps {
  status: string
  eventName: string
  id: string
  eventStatus: string
  onProceedToPayment?: (eventId: string) => void
  onEditPackages?: (eventId: string) => void
}

function SponsorshipActionsMenu({
  status,
  eventName,
  id,
  eventStatus,
  onProceedToPayment,
  onEditPackages,
}: Readonly<SponsorShipActionsMenuProps>) {
  const navigate = useNavigate()

  const isCompleted = isEventCompleted(eventStatus)

  const getMenuItems = () => {
    switch (status) {
      case 'Active':
        return [
          {
            label: 'Edit Ads',
            icon: EditIcon,
            action: () => console.log('Edit Ads for:', eventName),
          },
          {
            label: 'Upload Ads',
            icon: Upload2Icon,
            action: () => console.log('Upload Ads for:', eventName),
          },
        ]
      case 'Sponsorship Requested':
        return [
          {
            label: 'Edit Packages',
            icon: EditIcon,
            action: () => onEditPackages && onEditPackages(id),
          },
          {
            label: 'Proceed to Payment',
            icon: CardIcon,
            action: () => onProceedToPayment && onProceedToPayment(id),
          },
        ]
      case DISPLAY_SPONSORSHIP_STATUS.NOT_SELECTED:
      case DISPLAY_SPONSORSHIP_STATUS.FAILED:
        return [
          {
            label: 'Select Package',
            icon: PackageIcon,
            action: () =>
              navigate(`${PATHROUTES.DASHBOARD.SELECT_PACKAGE_WITH_ID(id)}`),
          },
        ]
      case 'Paid':
        return [
          {
            label: 'Upload Ads',
            icon: Upload2Icon,
            action: () =>
              navigate(`${PATHROUTES.DASHBOARD.UPLOAD_AD_WITH_ID(id)}`),
          },
        ]
      default:
        return []
    }
  }

  const menuItems = getMenuItems()

  if (menuItems.length === 0) {
    return null
  }

  const menuButton = (
    <Button
      variant="ghost"
      className={cn('h-8 w-8 p-0 rounded-md transition-colors')}
      aria-label={`More actions for ${eventName}`}
    >
      <MoreHorizontal className="h-5 w-5 text-ss-neutral-600" />
    </Button>
  )

  if (isCompleted) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="inline-block">{menuButton}</span>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[200px]">
            <p className="text-xs">Event completed. No actions available.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{menuButton}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-fit bg-white rounded-lg border border-ss-neutral-50 shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)] p-1"
        sideOffset={8}
      >
        {menuItems.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onClick={item.action}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-neutral-25 rounded-md cursor-pointer focus:bg-ss-neutral-25 focus:outline-none transition-colors"
          >
            <item.icon className="h-4 w-4" aria-hidden="true" />
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const createSponsorshipColumns = (
  onProceedToPayment?: (eventId: string) => void,
  onEditPackages?: (eventId: string) => void
): ColumnDef<DisplaySponsorEvent>[] => [
  {
    accessorKey: sponsorEventListAccessorKey.eventName,
    header: 'Event Name',
    cell: ({ row }) => {
      const event = row.original
      return (
        <Link
          to={PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(event.id)}
          className="font-semibold text-sm text-ss-text-primary leading-4.5  block max-w-[220px] wrap-anywhere transition-colors cursor-pointer hover:underline"
        >
          {row.getValue(sponsorEventListAccessorKey.eventName)}
        </Link>
      )
    },
  },
  {
    accessorKey: sponsorEventListAccessorKey.sponsorshipTier,
    header: 'Sponsorship Tier',
    cell: ({ row }) => (
      <span className="text-sm font-medium text-ss-text-primary max-w-24 wrap-anywhere">
        {capitalizeString(
          row.getValue(sponsorEventListAccessorKey.sponsorshipTier)
        )}
      </span>
    ),
  },
  {
    accessorKey: sponsorEventListAccessorKey.startDateTime,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        className="h-auto p-0 font-medium text-xs text-ss-text-primary wrap-anywhere transition-colors"
      >
        Start Date
        <ArrowUpDown className="ml-1 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <span className="text-sm font-medium text-ss-text-primary wrap-anywhere">
        {row.getValue(sponsorEventListAccessorKey.startDateTime)}
      </span>
    ),
    sortingFn: getSortingFn(
      sponsorEventListAccessorKey.startDateTime,
      DATATYPE.DATE
    ),
  },
  {
    accessorKey: sponsorEventListAccessorKey.endDateTime,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        className="h-auto p-0 font-medium text-xs text-ss-text-primary wrap-anywhere  transition-colors"
      >
        End Date
        <ArrowUpDown className="ml-1 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <span className="text-sm font-medium text-ss-text-primary wrap-anywhere">
        {row.getValue(sponsorEventListAccessorKey.endDateTime)}
      </span>
    ),
    sortingFn: getSortingFn(
      sponsorEventListAccessorKey.endDateTime,
      DATATYPE.DATE
    ),
  },
  {
    accessorKey: sponsorEventListAccessorKey.location,
    header: 'Location',
    cell: ({ row }) => (
      <span className="text-sm font-medium text-ss-text-primary max-w-[247px] wrap-anywhere  block">
        {row.getValue(sponsorEventListAccessorKey.location)}
      </span>
    ),
  },
  {
    accessorKey: sponsorEventListAccessorKey.sponsorshipStatus,
    header: 'Sponsorship Status',
    cell: ({ row }) => {
      const status = row.getValue(sponsorEventListAccessorKey.sponsorshipStatus)
      return (
        <Badge
          className={cn(
            'px-[10px] py-1 text-xs font-medium rounded-full border',
            paymentStatusClasses(status as SponsorshipStatus)
          )}
          aria-label={`Sponsorship status: ${status}`}
        >
          {status as SponsorshipStatus}
        </Badge>
      )
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const sponsorShip = row.original
      return (
        <SponsorshipActionsMenu
          status={row.original.sponsorshipStatus}
          eventName={row.original.eventName}
          id={sponsorShip.id}
          eventStatus={row.original.eventStatus}
          onProceedToPayment={onProceedToPayment}
          onEditPackages={onEditPackages}
        />
      )
    },
  },
]
